import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { authKeys } from '@/features/auth/api/auth.api';
import { isApiError } from '@/lib/api-error';

/**
 * Sends somebody back to the change-password screen when the server starts
 * refusing them for being on a temporary one.
 *
 * The server holds such an account on every route but the handful needed to get
 * out of it, and the route guard already redirects on `mustChangePassword` - but
 * it reads that off the cached session, which was fetched before the flag
 * changed. So an administrator resetting somebody's password mid-session left
 * them with a screen where every action failed and nothing explained why, until
 * they happened to reload.
 *
 * Re-reading the session is all it takes: `/auth/me` is one of the routes that
 * stays open, it comes back with the flag set, and the guard does the rest. No
 * loop, for the same reason - the refetch itself is not refused.
 */
function redirectIfHeldForPasswordChange(error: unknown): void {
  if (isApiError(error) && error.code === 'PASSWORD_CHANGE_REQUIRED') {
    void queryClient.invalidateQueries({ queryKey: authKeys.session() });
  }
}

/**
 * One shared cache for the whole app.
 *
 * The defaults matter: retrying a 401 or a 422 just delays the error the user
 * needs to see, so only genuine server and network failures are retried.
 */
export const queryClient = new QueryClient({
  /* On the caches rather than per query, because any call can be the one that
     discovers the account is being held - and a write is the likeliest, since
     that is what somebody tries first. */
  queryCache: new QueryCache({ onError: redirectIfHeldForPasswordChange }),
  mutationCache: new MutationCache({ onError: redirectIfHeldForPasswordChange }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (isApiError(error) && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      /* A failed write is reported to the user, never silently retried - it
         may not be idempotent. */
      retry: false,
    },
  },
});
